import type { Node } from 'react'
import React from 'react'
import { ButtonSmall, media, style } from 'app/assets/styles'
import { H4, H6, Text } from 'app/common/typography'
import Modal from 'react-modal'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { Close } from 'app/assets/icons'
import { COLOR, SPACING } from 'app-v2/design-tokens'
import { SmallText, Spacer } from 'app-v2/design-systems'

type Props = {
    alertTitle?: string,
    alertTitleKey?: string,
    alertTitleNode?: Node,
    alertContent?: string,
    alertContentKey?: string,
    alertMentionKey?: string,
    isOpen: boolean,
    loading?: boolean,
    handleClose: () => void,
    okLabel?: string,
    leftLabel?: string,
    onOkClick?: () => any,
    onLeftClick?: () => any,
    disabled?: boolean,
    illustration?: Node,
    disableClose?: boolean,
    children?: Node,
    forceButtonsOneLine?: boolean
}

export const AlertModal = ({
    alertTitle,
    alertTitleKey,
    alertTitleNode,
    alertContent,
    alertContentKey,
    alertMentionKey,
    disabled,
    isOpen,
    handleClose,
    okLabel,
    leftLabel,
    onOkClick,
    onLeftClick,
    illustration,
    disableClose,
    children,
    forceButtonsOneLine = false
}: Props) => {
    const intl = useIntl()
    return (
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            shouldCloseOnOverlayClick
            onRequestClose={handleClose}
            style={style.classicModalStyle}>
            <ResponsiveContainerPadding>
                {!disableClose && (
                    <CloseButtonContainer onClick={handleClose}>
                        <Close color={COLOR.PRIMARY} size={24} />
                    </CloseButtonContainer>
                )}
                {illustration && (
                    <StyledIllustrationContainer>
                        {illustration}
                    </StyledIllustrationContainer>
                )}
                <TextContainer>
                    {alertTitle && <H4 center>{alertTitle}</H4>}
                    {alertTitleKey && (
                        <H4 center>
                            {intl.formatMessage({ id: alertTitleKey })}
                        </H4>
                    )}
                    {!!alertTitleNode && alertTitleNode}
                </TextContainer>
                <TextContainer>
                    {alertContent && (
                        <StyledText center>{alertContent}</StyledText>
                    )}
                    {alertContentKey && (
                        <StyledText center>
                            {intl.formatMessage({ id: alertContentKey })}
                        </StyledText>
                    )}

                    {alertMentionKey && (
                        <>
                            <Spacer size={SPACING.XXS} />
                            <SmallText>
                                {intl.formatMessage({ id: alertMentionKey })}
                            </SmallText>
                        </>
                    )}
                </TextContainer>
                {children}
                <ButtonsContainer forceButtonsOneLine={forceButtonsOneLine}>
                    {leftLabel && (
                        <StyledButtonSmall
                            forceButtonsOneLine={forceButtonsOneLine}
                            color={COLOR.PRIMARY}
                            onClick={onLeftClick}
                            disabled={disabled}>
                            <H6 center>
                                {intl.formatMessage({ id: leftLabel })}
                            </H6>
                        </StyledButtonSmall>
                    )}
                    {okLabel && (
                        <StyledButtonSmall
                            forceButtonsOneLine={forceButtonsOneLine}
                            color="white"
                            ok
                            onClick={onOkClick}
                            disabled={disabled}>
                            <H6 center>
                                {intl.formatMessage({ id: okLabel })}
                            </H6>
                        </StyledButtonSmall>
                    )}
                </ButtonsContainer>
            </ResponsiveContainerPadding>
        </Modal>
    )
}

const ResponsiveContainerPadding = styled.div`
    min-width: 200px;
    padding: 20px;
    background: white;
    align-items: center;
    border-radius: 10px;
`

const StyledIllustrationContainer = styled.div`
    display: flex;
    padding: ${SPACING.M};
    align-items: center;
    justify-content: center;
`

const CloseButtonContainer = styled.div`
    position: absolute;
    top: 20px;
    cursor: pointer;
`

const ButtonsContainer = styled.div`
    width: 100%;
    margin: 15px 0;
    display: flex;
    flex-direction: column-reverse;
    ${media.medium`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    `};

    ${({ forceButtonsOneLine }) =>
        forceButtonsOneLine &&
        `
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: ${SPACING.M};
    `};
`

const StyledButtonSmall = styled(ButtonSmall)`
    ${props =>
        props.ok
            ? `background-color: ${COLOR.PRIMARY};`
            : `background-color: white;  margin-top: 10px;`};
    &:disabled {
        opacity: 0.5;
    }
    border: 1px solid ${COLOR.PRIMARY};
    border-radius: 10px;
    display: block;
    width: 100%;
    ${media.medium`
        margin: 0 5px;
        width: unset;
        flex-grow: 1;
    `};

    ${({ forceButtonsOneLine }) =>
        forceButtonsOneLine &&
        `
        margin: 0 5px;
        width: unset;
        flex-grow: 1;
    `};
`

const TextContainer = styled.div`
    padding: 0 ${SPACING.S} ${SPACING.S};
`

const StyledText = styled(Text)`
    white-space: pre-line;
`
